import { collidesWalls } from '../lib/geometry.js';
import type { World } from '../world/types.js';
import { STATE } from '../world/types.js';
import { input } from '../input/keyboard.js';
import { canExtract, updatePickups } from './objective.js';

const SPEED_BASE = 150;
const SPEED_IN_BOX = 70;
const SPEED_BODY_MULT = 0.55;
const EXIT_RADIUS = 22;

export function updatePlayer(world: World, dt: number, onWin: () => void): void {
  if (world.state !== STATE.PLAY) return;

  const { player } = world;
  const { keys, joyVec } = input;

  let ix = 0;
  let iy = 0;
  if (keys.KeyA || keys.ArrowLeft) ix -= 1;
  if (keys.KeyD || keys.ArrowRight) ix += 1;
  if (keys.KeyW || keys.ArrowUp) iy -= 1;
  if (keys.KeyS || keys.ArrowDown) iy += 1;
  ix += joyVec.x;
  iy += joyVec.y;

  let m = Math.hypot(ix, iy);
  if (m > 1) {
    ix /= m;
    iy /= m;
    m = 1;
  }
  if (m > 0.05) player.dir = Math.atan2(iy, ix);

  let speed = SPEED_BASE;
  if (player.inBox) speed = SPEED_IN_BOX;
  if (player.carryingBody) speed *= SPEED_BODY_MULT;

  const beforeX = player.x;
  const beforeY = player.y;
  const nx = player.x + ix * speed * dt;
  const ny = player.y + iy * speed * dt;
  if (!collidesWalls(world, nx, player.y, player.r)) player.x = nx;
  if (!collidesWalls(world, player.x, ny, player.r)) player.y = ny;
  // Per-frame velocity in px/s, after collision response.
  // Used by main.ts to drive footstep audio.
  player.vx = dt > 0 ? (player.x - beforeX) / dt : 0;
  player.vy = dt > 0 ? (player.y - beforeY) / dt : 0;

  // Drive the walking animation accumulator. The renderer uses
  // sin(walkT) for head bob + arm swing — increment proportional to
  // actual ground speed so it slows naturally with the box / body drag.
  const walkSpeed = Math.hypot(player.vx, player.vy);
  if (walkSpeed > 12) player.walkT += dt * (walkSpeed / 30);

  if (player.inBox && world.activeBox) {
    world.activeBox.x = player.x;
    world.activeBox.y = player.y;
  }

  // Pickup any intel files the player walked over.
  updatePickups(world);

  // Win check — extraction is gated by the objective state.
  if (canExtract(world, EXIT_RADIUS)) {
    onWin();
  }
}
